const API = typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1"
    ? "https://ftr-api.ftrai.uk"
    : (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080");

export interface NowPlaying {
  playing: boolean;
  artist: string;
  track: string;
  album_art: string;
  progress_ms: number;
  duration_ms: number;
}

export interface SearchTrack {
  title: string;
  artist: string;
  uri: string;
  album_art: string;
  duration_ms: number;
}

export interface Status {
  online: boolean;
  listeners: number;
}

export interface AnnounceResult {
  script?: string;
}

export async function getNowPlaying(): Promise<NowPlaying> {
  const res = await fetch(`${API}/nowplaying`);
  return res.json();
}

export async function search(q: string): Promise<{ tracks: SearchTrack[] }> {
  const res = await fetch(`${API}/search?q=${encodeURIComponent(q)}`);
  return res.json();
}

export interface QueueAddResult {
  status: "queued" | "duplicate" | "blocked";
  uri: string;
  retry_after_seconds?: number;
  reason?: string;
}

export interface QueueAddPayload {
  uri: string;
  title?: string;
  artist?: string;
  album_art?: string;
}

export async function addToQueue(payload: QueueAddPayload): Promise<QueueAddResult> {
  const res = await fetch(`${API}/queue/add`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export interface HostRequest {
  id: number;
  uri: string;
  title: string;
  artist: string;
  album_art?: string | null;
  requested_at: number;
  status: "queued" | "removed" | "blocked" | "duplicate";
}

export async function getRequests(password: string): Promise<{ requests: HostRequest[]; blocked_uris: string[] }> {
  const res = await fetch(`${API}/requests`, {
    headers: { "x-password": password },
  });
  return res.json();
}

export async function removeRequest(id: number, password: string): Promise<{ status: string; uri: string; skipped_now: boolean }> {
  const res = await fetch(`${API}/requests/${id}/remove`, {
    method: "POST",
    headers: { "x-password": password },
  });
  return res.json();
}

export async function getStatus(): Promise<Status> {
  const res = await fetch(`${API}/status`);
  return res.json();
}

export async function getQueue(): Promise<{ queue: { title: string; artist: string; album_art?: string; uri?: string }[] }> {
  const res = await fetch(`${API}/queue`);
  return res.json();
}

export async function announceRaw(text: string, password: string, now = false): Promise<void> {
  await fetch(`${API}/announce/raw`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-password": password,
    },
    body: JSON.stringify({ text, now }),
  });
}

export interface ModeState {
  mode: string;
  scheduler_running?: boolean;
  scheduler_pids?: number[];
}

export async function getMode(): Promise<ModeState> {
  const res = await fetch(`${API}/mode`);
  return res.json();
}

export async function setMode(mode: "jukebox" | "ai-dj", password: string): Promise<ModeState> {
  const res = await fetch(`${API}/mode`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-password": password,
    },
    body: JSON.stringify({ mode }),
  });
  return res.json();
}

export async function announceAI(prompt: string, password: string, now = false): Promise<AnnounceResult> {
  const res = await fetch(`${API}/announce/ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-password": password,
    },
    body: JSON.stringify({ prompt, now }),
  });
  return res.json();
}

export interface Playlist {
  name: string;
  uri: string;
  owner: string;
  tracks: number;
  image: string | null;
}

export async function searchPlaylists(q: string): Promise<{ playlists: Playlist[] }> {
  const res = await fetch(`${API}/playlists/search?q=${encodeURIComponent(q)}`);
  return res.json();
}

export async function playPlaylist(uri: string, password: string): Promise<void> {
  await fetch(`${API}/playlists/play`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-password": password,
    },
    body: JSON.stringify({ uri }),
  });
}
